import { createContext, useContext, useCallback, useEffect, useReducer } from 'react';
import api, { refreshSession } from '@utils/api';

// ─── State ────────────────────────────────────────────────────────────────────
const initialState = {
  user:        null,
  token:       null,
  isLoading:   true,
  isAuthenticated: false,
  error:       null,
};

function authReducer(state, action) {
  switch (action.type) {
    case 'LOGIN_SUCCESS':
      return { ...state, user: action.user, token: action.token, isAuthenticated: true, isLoading: false, error: null };
    case 'LOGOUT':
      return { ...initialState, isLoading: false };
    case 'REFRESH_SUCCESS':
      // Keep isLoading true: the token is refreshed but the user profile is
      // still in flight. Dropping isLoading here makes ProtectedRoute see
      // "not loading + not authenticated" and bounce the reload to /login.
      return { ...state, token: action.token };
    case 'SET_LOADING':
      return { ...state, isLoading: action.value };
    case 'SET_ERROR':
      return { ...state, error: action.error, isLoading: false };
    default:
      return state;
  }
}

// ─── Context ──────────────────────────────────────────────────────────────────
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Try to restore session on mount. Uses the shared single-flight refresh so
  // it can never race the axios interceptor's refresh (which would rotate the
  // one-time cookie twice and log the user out). A cold-start 401 here just
  // means "no session" — caught quietly, no error surfaced.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await refreshSession();
        if (cancelled) return;
        api.defaults.headers.common['Authorization'] = `Bearer ${data.accessToken}`;
        dispatch({ type: 'REFRESH_SUCCESS', token: data.accessToken });
        const { data: user } = await api.get('/auth/me');
        if (cancelled) return;
        dispatch({ type: 'LOGIN_SUCCESS', user, token: data.accessToken });
      } catch {
        if (!cancelled) dispatch({ type: 'LOGOUT' });
      }
    })();

    // The interceptor fires this when a silent refresh ultimately fails.
    const onExpired = () => dispatch({ type: 'LOGOUT' });
    window.addEventListener('gl:session-expired', onExpired);
    return () => {
      cancelled = true;
      window.removeEventListener('gl:session-expired', onExpired);
    };
  }, []);

  const login = useCallback(async (email, password) => {
    dispatch({ type: 'SET_LOADING', value: true });
    try {
      const { data } = await api.post('/auth/login', { email, password });
      api.defaults.headers.common['Authorization'] = `Bearer ${data.accessToken}`;
      dispatch({ type: 'LOGIN_SUCCESS', user: data.user, token: data.accessToken });
      return { success: true };
    } catch (err) {
      const error = err.response?.data?.message || 'Login failed';
      dispatch({ type: 'SET_ERROR', error });
      return { success: false, error };
    }
  }, []);

  const logout = useCallback(async () => {
    try { await api.post('/auth/logout'); } catch { /* ignore */ }
    delete api.defaults.headers.common['Authorization'];
    dispatch({ type: 'LOGOUT' });
  }, []);

  const hasRole = useCallback((requiredRoles) => {
    if (!state.user) return false;
    if (!Array.isArray(requiredRoles)) requiredRoles = [requiredRoles];
    return requiredRoles.includes(state.user.role);
  }, [state.user]);

  const hasPlatformAccess = useCallback((platformId) => {
    if (!state.user) return false;
    if (state.user.role === 'super_admin' || state.user.role === 'admin') return true;
    return state.user.platformAccess?.includes(platformId) ?? false;
  }, [state.user]);

  return (
    <AuthContext.Provider value={{ ...state, login, logout, hasRole, hasPlatformAccess }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
